import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CircleAlert, ImageOff, ShieldCheck, TriangleAlert } from "lucide-react";
import { errMsg, useApi } from "@/lib/api";

const place = (w) => [w.page, w.section && `sezione «${w.section}»`].filter(Boolean).join(" · ");

function Problem({ p, pid, i }) {
  const Icon = p.level === "error" ? CircleAlert : TriangleAlert;
  return (
    <li className="py-2.5" data-testid={`image-gate-problem-${i}`}>
      <p className={`flex items-start gap-2 text-sm font-medium ${p.level === "error" ? "text-red-700" : "text-amber-800"}`}>
        <Icon size={15} className="mt-0.5 shrink-0" />{p.title}<span className="text-xs font-normal text-slate-500">· {p.count} {p.count === 1 ? "posizione" : "posizioni"}</span>
      </p>
      <p className="ml-6 mt-0.5 text-xs text-slate-600">{p.detail}</p>
      {p.url && <p className="ml-6 mt-1 truncate font-mono text-[11px] text-slate-500" data-testid={`image-gate-problem-url-${i}`}>{p.url}</p>}
      <p className="ml-6 mt-1 flex flex-wrap gap-1">{p.where.map((w, k) => <code key={k} className="rounded bg-white px-1.5 py-0.5 text-[11px] text-slate-600 ring-1 ring-slate-200">{place(w)}</code>)}</p>
      <Link to={`/projects/${pid}`} className="ml-6 mt-1.5 inline-block text-xs font-medium text-sky-700 hover:underline" data-testid={`image-gate-fix-link-${i}`}>Correggi nell'editor →</Link>
    </li>
  );
}

export default function ImageGateCard({ projectId, siteId, refreshKey }) {
  const api = useApi();
  const [rep, setRep] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get(`/projects/${projectId}/publish-check`, { params: { site_id: siteId } }).then(({ data }) => { setRep(data); setErr(""); }).catch((e) => setErr(errMsg(e)));
  }, [api, projectId, siteId, refreshKey]);
  if (err) return <p className="text-xs text-red-600" data-testid="image-gate-error">Controllo immagini non disponibile: {err}</p>;
  if (!rep || !rep.ai_images) return null;
  const c = rep.counts || {};
  const problems = [...rep.errors, ...rep.warnings];
  const ok = rep.ok;
  return (
    <section className={`rounded-xl border px-5 py-4 ${ok ? "border-emerald-200 bg-emerald-50/50" : "border-red-200 bg-red-50/50"}`} data-testid="image-gate-card">
      <div className="flex flex-wrap items-center gap-2">
        {ok ? <ShieldCheck size={17} className="text-emerald-600" /> : <ImageOff size={17} className="text-red-600" />}
        <h2 className={`text-sm font-semibold ${ok ? "text-emerald-800" : "text-red-800"}`} data-testid="image-gate-status">
          {ok ? "Immagini AI pronte per la pubblicazione" : `Pubblicazione bloccata: ${rep.errors.length} ${rep.errors.length === 1 ? "problema" : "problemi"} con le immagini`}
        </h2>
      </div>
      <p className="mt-1 text-xs text-slate-600" data-testid="image-gate-counts">
        Sito con immagini AI: si pubblicano solo foto del cliente e immagini AI verificate o approvate, copiate nel sito (WebP + JPEG di riserva). {c.images || 0} immagini usate · {c.client || 0} del cliente · {(c.ai_verified || 0) + (c.ai_approved || 0) + (c.ai_technical || 0)} AI{c.ai_approved ? ` (${c.ai_approved} approvate a mano)` : ""}.
      </p>
      {problems.length > 0 && <ul className="mt-2 divide-y divide-white/70" data-testid="image-gate-problems">{problems.map((p, i) => <Problem key={`${p.code}-${p.url}-${i}`} p={p} pid={projectId} i={i} />)}</ul>}
    </section>
  );
}
